import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { User, ArrowLeft, MessageSquare, MapPin, Briefcase, Music, Calendar, Users, DollarSign } from 'lucide-react';
import '../styles/personaProfile.css';

function PersonaProfile() {
  const { foundationId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [persona, setPersona] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedOriginStory, setExpandedOriginStory] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const fetchPersona = async () => {
      try {
        setLoading(true);
        setError(null);

        const queryParams = new URLSearchParams();
        queryParams.append('foundation_id', foundationId);
        queryParams.append('limit', '1');

        const response = await fetch(`https://n8n.iniai.cloud/webhook/get-persona-details?${queryParams.toString()}`, {
          method: 'GET',
          headers: {
            'x-api-key': process.env.REACT_APP_CREATE_USER_API_KEY,
            'Content-Type': 'application/json'
          },
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        if (data && data.length > 0) {
          const rawPersona = data[0];

          // Transform data to match UI format
          const transformedPersona = {
            id: rawPersona.foundation_id,
            name: rawPersona.profile_name.replace(/_/g, ' '),
            title: extractTitle(rawPersona.demographic_summary),
            industry: extractIndustry(rawPersona.foundation_id, rawPersona.cultural_context),
            department: extractDepartment(rawPersona.demographic_summary, rawPersona.cultural_context),
            experience: extractExperience(rawPersona.demographic_summary),
            company: extractCompany(rawPersona.cultural_context, rawPersona.demographic_summary),
            description: rawPersona.demographic_summary || 'Music fan profile',
            traits: extractTraits(rawPersona.demographic_summary, rawPersona.cultural_context),
            // Full profile data for detailed view
            profileData: rawPersona.profile_data,
            original: {
              foundation_id: rawPersona.foundation_id,
              profile_name: rawPersona.profile_name,
              demographic_summary: rawPersona.demographic_summary,
              cultural_context: rawPersona.cultural_context,
              profile_data: rawPersona.profile_data,
              generation_target: rawPersona.generation_target,
              generation_prompt: rawPersona.generation_prompt
            }
          };

          setPersona(transformedPersona);
        } else {
          setError('Persona not found');
        }
      } catch (error) {
        console.error('Error fetching persona:', error);
        setError('Failed to load persona. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (foundationId) {
      fetchPersona();
    }
  }, [foundationId]);

  // Helper functions (same as PersonaInterview.js)
  const extractTitle = (demographic) => {
    if (!demographic) return 'Fan';
    const parts = demographic.split(',').map(p => p.trim());
    if (parts.length >= 2) {
      const potentialTitle = parts[1];
      if (potentialTitle && !potentialTitle.match(/^\$|\d+K|years?\s+clean|male|female/i)) {
        return potentialTitle;
      }
    }
    const patterns = [
      /(?:works? as|job|occupation|is)[:\s]+([\w\s]+?)(?:[,.\n]|$)/i,
      /(doctor|nurse|teacher|engineer|developer|manager|consultant|analyst|designer|chef|bartender|electrician|plumber|lawyer|accountant|student|researcher|artist|writer|musician|operator|bookstore\s+manager|development\s+coordinator|record\s+store\s+owner|casting\s+director\s+assistant|marketing\s+coordinator|dental\s+hygienist|claims\s+adjuster|music\s+journalist|blogger|project\s+manager|communications\s+coordinator|uber\s+driver|shift\s+supervisor|qa\s+tester)/i,
      /(\w+)\s+(?:in|at)\s+(?:the\s+)?[\w\s]+/i
    ];
    for (const pattern of patterns) {
      const match = demographic.match(pattern);
      if (match && match[1]) {
        const title = match[1].trim();
        return title.replace(/^(a|an|the)\s+/i, '');
      }
    }
    return 'Fan';
  };

  const extractIndustry = (foundationId, cultural) => {
    if (foundationId && foundationId.includes('_')) {
      const artistPart = foundationId.split('_').slice(0, -2).join(' ');
      const artistName = artistPart.replace(/_/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      return `${artistName} Audience`;
    }
    if (cultural) {
      const text = cultural.toLowerCase();
      if (text.includes('tech') || text.includes('software')) return 'Technology';
      if (text.includes('healthcare') || text.includes('medical')) return 'Healthcare';
      if (text.includes('education') || text.includes('school')) return 'Education';
      if (text.includes('finance') || text.includes('bank')) return 'Finance';
    }
    return 'Music Audience';
  };

  const extractDepartment = (demographic, cultural) => {
    if (!demographic && !cultural) return 'Fanbase';
    const text = `${demographic || ''} ${cultural || ''}`.toLowerCase();
    if (text.includes('engineer') || text.includes('developer')) return 'Engineering';
    if (text.includes('doctor') || text.includes('nurse')) return 'Medical';
    if (text.includes('teacher') || text.includes('professor')) return 'Education';
    if (text.includes('manager') || text.includes('director')) return 'Management';
    if (text.includes('student')) return 'Student Life';
    if (text.includes('union') || text.includes('trade')) return 'Labor';
    return 'Community';
  };

  const extractExperience = (demographic) => {
    if (!demographic) return 'Music Fan';
    const text = demographic.toLowerCase();
    if (text.includes('senior') || text.includes('veteran') || text.includes('experienced')) return 'Experienced';
    if (text.includes('junior') || text.includes('entry') || text.includes('new')) return 'Entry Level';
    if (text.includes('student') || text.includes('recent')) return 'Student/Recent';
    return 'Professional';
  };

  const extractCompany = (cultural, demographic) => {
    if (!cultural && !demographic) return 'Independent';
    const text = `${cultural || ''} ${demographic || ''}`;
    const workMatch = text.match(/(?:works? at|company|employed by)[:\s]+([^,.\n]+)/i);
    if (workMatch) return workMatch[1].trim();
    if (cultural) {
      let location = cultural
        .replace(/\s+Context$/, '')
        .replace(/Music Scene|Tech Industry|Indie Scene|Urban Economic Pressure|Class Dynamics/, '')
        .replace(/\s+$/, '')
        .split('/')[0]
        .split(',')[0];
      if (location.includes('Appalachian')) return location;
      if (location.includes('Eastern Kentucky')) return 'Eastern Kentucky';
      if (location.includes('Kentucky') && location.includes('Nashville')) return 'Kentucky/Nashville';
      if (location.includes('Brooklyn')) return 'Brooklyn, NY';
      if (location.includes('Capitol Hill')) return 'Seattle, WA';
      if (location.includes('Bay Area') || location.includes('Silicon Valley')) return 'San Francisco Bay Area';
      if (location.includes('Rust Belt')) return 'Pittsburgh, PA';
      if (location.length > 2) return location;
    }
    return 'Various';
  };

  const extractTraits = (demographic, cultural) => {
    const traits = [];
    const text = `${demographic || ''} ${cultural || ''}`.toLowerCase();
    if (text.includes('non-binary') || text.includes('nonbinary') || text.includes('non binary')) {
      traits.push('Non-binary');
    } else if (text.includes('female')) {
      traits.push('Female');
    } else if (text.includes(' male') || text.match(/\bmale\b/)) {
      traits.push('Male');
    }
    if (text.includes('recovering') || text.includes('clean') || text.includes('sober')) traits.push('In Recovery');
    if (text.includes('single parent') || text.includes('single mom') || text.includes('single dad')) traits.push('Single Parent');
    else if (text.includes('parent')) traits.push('Parent');
    if (text.includes('union') && text.includes('professional')) {
      traits.push('Union Professional');
    } else if (text.includes('union') || (text.includes('worker') && !text.includes('professional'))) {
      traits.push('Working Class');
    } else if (text.includes('professional')) {
      traits.push('Professional');
    }
    if (text.includes('college') || text.includes('university')) traits.push('College Educated');
    if (text.includes('student')) traits.push('Student');
    if (text.includes('local') || text.includes('community')) traits.push('Community Oriented');
    return traits.length > 0 ? traits.slice(0, 3) : ['Music Fan'];
  };

  const startInterview = () => {
    // Ensure persona has the same structure expected by PersonaInterview
    const interviewPersona = {
      ...persona,
      // Make sure original data is properly structured for the interview request
      original: {
        foundation_id: persona.original?.foundation_id || persona.id,
        profile_name: persona.original?.profile_name || persona.name.replace(/\s+/g, '_'),
        demographic_summary: persona.original?.demographic_summary || persona.description,
        cultural_context: persona.original?.cultural_context || persona.company,
        profile_data: persona.original?.profile_data || persona.industry,
        generation_target: persona.original?.generation_target || persona.industry?.replace(' Audience', '') || 'Jelly Roll',
        generation_prompt: persona.original?.generation_prompt || ''
      }
    };
    navigate('/persona-interview', { state: { selectedPersona: interviewPersona } });
  };

  if (loading) {
    return (
      <div className="profile-loading">
        <div className="loading-spinner"></div>
        <p>Loading persona profile...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="profile-error">
        <p>{error}</p>
        <button onClick={() => navigate('/persona-interview')} className="back-btn">
          ← Back to Personas
        </button>
      </div>
    );
  }

  if (!persona) {
    return (
      <div className="profile-error">
        <p>Persona not found</p>
        <button onClick={() => navigate('/persona-interview')} className="back-btn">
          ← Back to Personas
        </button>
      </div>
    );
  }

  const profileData = persona.profileData || {};

  return (
    <div className="persona-profile-container">
      {/* Page Header */}
      <div className="page-header">
        <button onClick={() => navigate('/persona-interview')} className="back-btn">
          <ArrowLeft size={16} />
          Back to Personas
        </button>

        <div className="header-content">
          <div className="header-title">
            <User size={32} />
            <h1>{persona.name}</h1>
          </div>
        </div>

        <button onClick={startInterview} className="interview-btn">
          <MessageSquare size={16} />
          Start Interview
        </button>
      </div>

      {/* Profile Summary Card */}
      <div className="profile-summary-card">
        <div className="summary-content">
          <div className="summary-avatar">
            <User size={48} />
          </div>
          <div className="summary-info">
            <p className="summary-description">{persona.description}</p>
            <div className="profile-traits">
              {persona.traits.map(trait => (
                <span key={trait} className="trait-tag">{trait}</span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="tabs-navigation">
        <button
          className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <Briefcase size={16} />
          <span>Overview</span>
        </button>
        <button
          className={`tab-btn ${activeTab === 'psychology' ? 'active' : ''}`}
          onClick={() => setActiveTab('psychology')}
        >
          <User size={16} />
          <span>Psychology</span>
        </button>
        <button
          className={`tab-btn ${activeTab === 'music-artists' ? 'active' : ''}`}
          onClick={() => setActiveTab('music-artists')}
        >
          <Music size={16} />
          <span>Music & Artists</span>
        </button>
        <button
          className={`tab-btn ${activeTab === 'brands' ? 'active' : ''}`}
          onClick={() => setActiveTab('brands')}
        >
          <DollarSign size={16} />
          <span>Brands</span>
        </button>
        <button
          className={`tab-btn ${activeTab === 'experiences' ? 'active' : ''}`}
          onClick={() => setActiveTab('experiences')}
        >
          <Calendar size={16} />
          <span>Experiences</span>
        </button>
      </div>

      {/* Tab Content */}
      <div className="profile-content">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="tab-content">
        {/* Basic Info */}
        <div className="profile-section">
          <h2>Basic Information</h2>
          <div className="info-grid">
            <div className="info-item">
              <MapPin size={16} />
              <div>
                <span className="info-label">Location</span>
                <span className="info-value">{profileData.location || persona.company}</span>
              </div>
            </div>
            <div className="info-item">
              <Briefcase size={16} />
              <div>
                <span className="info-label">Occupation</span>
                <span className="info-value">{profileData.job_title || persona.title}</span>
              </div>
            </div>
            <div className="info-item">
              <Music size={16} />
              <div>
                <span className="info-label">Music Community</span>
                <span className="info-value">{persona.industry}</span>
              </div>
            </div>
            {profileData.salary && (
              <div className="info-item">
                <DollarSign size={16} />
                <div>
                  <span className="info-label">Income</span>
                  <span className="info-value">{profileData.salary}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Economic Reality */}
        {profileData.core_identity?.economic_reality && (
          <div className="profile-section">
            <h2>Economic Reality</h2>
            <div className="story-content">
              <p>{profileData.core_identity.economic_reality}</p>
            </div>
          </div>
        )}

        {/* Artist Connection */}
        {profileData.artist_connection && (
          <div className="profile-section artist-connection-section">
            <h2>Artist Connection</h2>
            <div className="story-content">
              <p>{profileData.artist_connection}</p>
            </div>
          </div>
        )}

        {/* Origin Story - Collapsible */}
        {profileData.origin_story && (
          <div className="profile-section">
            <h2>Background Story</h2>
            <div className="story-content">
              <p className={expandedOriginStory ? 'story-expanded' : 'story-collapsed'}>
                {profileData.origin_story}
              </p>
              <button
                onClick={() => setExpandedOriginStory(!expandedOriginStory)}
                className="expand-story-btn"
              >
                {expandedOriginStory ? '← Show Less' : 'Read Full Story →'}
              </button>
            </div>
          </div>
        )}

        {/* Core Identity */}
        {profileData.core_identity && (
          <div className="profile-section">
            <h2>Core Identity</h2>
            <div className="identity-grid">
              {profileData.core_identity.age_range && (
                <div className="identity-item">
                  <Calendar size={16} />
                  <div>
                    <span className="info-label">Age Range</span>
                    <span className="info-value">{profileData.core_identity.age_range}</span>
                  </div>
                </div>
              )}
              {profileData.core_identity.demographic_frame && (
                <div className="identity-item">
                  <Users size={16} />
                  <div>
                    <span className="info-label">Demographics</span>
                    <span className="info-value">{profileData.core_identity.demographic_frame}</span>
                  </div>
                </div>
              )}
            </div>
            {profileData.core_identity.cultural_context_hints && (
              <div className="cultural-context">
                <h3>Cultural Context</h3>
                <p>{profileData.core_identity.cultural_context_hints}</p>
              </div>
            )}
          </div>
        )}

        {/* Interview Readiness */}
        {profileData.interview_readiness_test && (
          <div className="profile-section">
            <h2>Great Interview Questions</h2>
            <div className="interview-questions">
              {profileData.interview_readiness_test.map((question, index) => (
                <div key={index} className="question-card">
                  <p>"{question}"</p>
                </div>
              ))}
            </div>
          </div>
        )}
          </div>
        )}

        {/* Psychology Tab */}
        {activeTab === 'psychology' && (
          <div className="tab-content">

        {/* Core Psychology */}
        {profileData.core_psychology && (
          <div className="profile-section core-psychology-section">
            <h2>Core Psychology</h2>
            <div className="story-content">
              <p>{profileData.core_psychology}</p>
            </div>
          </div>
        )}

        {/* Social Environment */}
        {profileData.core_identity?.social_environment && (
          <div className="profile-section">
            <h2>Social Environment</h2>
            <div className="story-content">
              <p>{profileData.core_identity.social_environment}</p>
            </div>
          </div>
        )}

        {/* Communication Style */}
        {(profileData.foundation_pysch_context?.communication_style || profileData.communication_style) && (
          <div className="profile-section">
            <h2>Communication Style</h2>
            {(() => {
              const commStyle = profileData.foundation_pysch_context?.communication_style || profileData.communication_style;
              return (
                <div className="communication-grid">
                  {commStyle.emotional_expression && (
                    <div className="comm-item">
                      <h3>Emotional Expression</h3>
                      <p>{commStyle.emotional_expression}</p>
                    </div>
                  )}
                  {commStyle.authentic_voice_patterns && (
                    <div className="comm-item">
                      <h3>Authentic Voice</h3>
                      <p>{commStyle.authentic_voice_patterns}</p>
                    </div>
                  )}
                  {commStyle.social_performance_speech && (
                    <div className="comm-item">
                      <h3>Social Performance</h3>
                      <p>{commStyle.social_performance_speech}</p>
                    </div>
                  )}
                  {commStyle.professional_communication && (
                    <div className="comm-item">
                      <h3>Professional</h3>
                      <p>{commStyle.professional_communication}</p>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* Behavioral Contradictions */}
        {(profileData.foundation_pysch_context?.behavioral_contradictions || profileData.behavioral_contradictions) &&
         ((profileData.foundation_pysch_context?.behavioral_contradictions || profileData.behavioral_contradictions).length > 0) && (
          <div className="profile-section">
            <h2>Behavioral Contradictions</h2>
            <div className="contradictions-list">
              {(profileData.foundation_pysch_context?.behavioral_contradictions || profileData.behavioral_contradictions).map((contradiction, index) => (
                <div key={index} className="contradiction-card">
                  <div className="contradiction-layer">
                    <span className="layer-label">Surface</span>
                    <p>{contradiction.surface_behavior}</p>
                  </div>
                  <div className="contradiction-layer">
                    <span className="layer-label">Internal Conflict</span>
                    <p>{contradiction.internal_conflict}</p>
                  </div>
                  <div className="contradiction-layer">
                    <span className="layer-label">Underlying Reality</span>
                    <p>{contradiction.underlying_reality}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Primary Drivers */}
        {profileData.primary_drivers && profileData.primary_drivers.length > 0 && (
          <div className="profile-section">
            <h2>Primary Behavioral Drivers</h2>
            <div className="drivers-list">
              {profileData.primary_drivers.map((driver, index) => (
                <div key={index} className="driver-card">
                  <h3>{driver.behavior}</h3>
                  <div className="driver-intensity">
                    <span className={`intensity-badge ${driver.intensity}`}>{driver.intensity} intensity</span>
                  </div>
                  <p>{driver.manifestation}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Speech Patterns */}
        {profileData.speech_and_linguistic_profile && (
          <div className="profile-section">
            <h2>Speech & Linguistic Profile</h2>
            <div className="speech-patterns">
              {profileData.speech_and_linguistic_profile.contextual_speech_variations && (
                <div className="speech-contexts">
                  {Object.entries(profileData.speech_and_linguistic_profile.contextual_speech_variations).map(([context, variations]) => (
                    <div key={context} className="context-card">
                      <h3>{context.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</h3>
                      {Object.entries(variations).map(([category, phrases]) => (
                        <div key={category} className="phrase-category">
                          <h4>{category.replace(/_/g, ' ')}</h4>
                          <ul>
                            {phrases.slice(0, 3).map((phrase, i) => (
                              <li key={i}>"{phrase}"</li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Primary Behaviors */}
        {profileData.primary_behaviors && profileData.primary_behaviors.length > 0 && (
          <div className="profile-section">
            <h2>Primary Behaviors</h2>
            <div className="behaviors-list">
              {profileData.primary_behaviors.map((behavior, index) => (
                <div key={index} className="behavior-item">
                  <p>{behavior}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Secondary Patterns */}
        {profileData.secondary_patterns && profileData.secondary_patterns.length > 0 && (
          <div className="profile-section">
            <h2>Secondary Behavioral Patterns</h2>
            <div className="secondary-patterns-list">
              {profileData.secondary_patterns.map((pattern, index) => (
                <div key={index} className="pattern-card">
                  <h3>{pattern.behavior}</h3>
                  {pattern.trigger_conditions && (
                    <div className="pattern-triggers">
                      <span className="pattern-label">Triggered by:</span>
                      <span className="pattern-value">{pattern.trigger_conditions}</span>
                    </div>
                  )}
                  {pattern.relationship_to_primary && (
                    <p className="pattern-relationship">{pattern.relationship_to_primary}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Coping Mechanisms */}
        {profileData.coping_mechanisms && profileData.coping_mechanisms.length > 0 && (
          <div className="profile-section">
            <h2>Coping Mechanisms</h2>
            <div className="coping-list">
              {profileData.coping_mechanisms.map((mechanism, index) => (
                <div key={index} className="coping-item">
                  <p>{mechanism}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Self-Awareness Gaps */}
        {profileData.self_awareness_gaps && profileData.self_awareness_gaps.length > 0 && (
          <div className="profile-section">
            <h2>Self-Awareness Gaps</h2>
            <div className="awareness-gaps-list">
              {profileData.self_awareness_gaps.map((gap, index) => (
                <div key={index} className="awareness-gap-item">
                  <p>{gap}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Psychological Triggers */}
        {profileData.psychological_triggers && (
          <div className="profile-section">
            <h2>Key Topics & Triggers</h2>
            <div className="triggers-list">
              {profileData.psychological_triggers.map((trigger, index) => (
                <div key={index} className="trigger-item">
                  <p>{trigger}</p>
                </div>
              ))}
            </div>
          </div>
        )}
          </div>
        )}

        {/* Music & Artists Tab */}
        {activeTab === 'music-artists' && (
          <div className="tab-content">

        {/* Musical Autobiography */}
        {profileData.musical_autobiography && (
          <div className="profile-section">
            <h2>Musical Journey</h2>

            {/* Genre Evolution */}
            {profileData.musical_autobiography.genre_evolution && (
              <div className="genre-evolution">
                <h3>Genre Evolution</h3>
                <p>{profileData.musical_autobiography.genre_evolution}</p>
              </div>
            )}

            {/* Discovery Periods */}
            {profileData.musical_autobiography.discovery_periods && (
              <div className="discovery-timeline">
                {profileData.musical_autobiography.discovery_periods.map((period, index) => (
                  <div key={index} className="timeline-item">
                    <div className="timeline-marker"></div>
                    <div className="timeline-content">
                      <h3>{period.title || period.life_stage}</h3>
                      <span className="period-date">{period.period}</span>
                      <p>{period.behavioral_development}</p>
                      {period.key_artists && period.key_artists.length > 0 && (
                        <div className="key-artists">
                          <h4>Key Artists</h4>
                          <div className="artists-list">
                            {period.key_artists.map((artist, artistIndex) => (
                              <div key={artistIndex} className="artist-item-detailed">
                                <div className="artist-header">
                                  <span className="artist-name">{artist.name || artist}</span>
                                </div>
                                {artist.discovery_context && (
                                  <span className="discovery-context">{artist.discovery_context}</span>
                                )}
                                {artist.specific_behavior && (
                                  <span className="artist-behavior">{artist.specific_behavior}</span>
                                )}
                                {artist.psychological_trigger && (
                                  <span className="artist-trigger">💡 {artist.psychological_trigger}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {period.cultural_influences && (
                        <div className="cultural-influences">
                          <h4>Cultural Influences</h4>
                          <p>{period.cultural_influences}</p>
                        </div>
                      )}
                      {period.key_brands && period.key_brands.length > 0 && (
                        <div className="key-brands">
                          <h4>Key Brands & Products</h4>
                          <div className="brands-grid">
                            {period.key_brands.map((brand, brandIndex) => (
                              <div key={brandIndex} className="brand-card">
                                <div className="brand-header">
                                  <h5>{brand.name}</h5>
                                  <span className="brand-category">{brand.category}</span>
                                </div>
                                {brand.current_obsession && (
                                  <p className="brand-obsession">{brand.current_obsession}</p>
                                )}
                                {brand.discovery_context && (
                                  <div className="brand-detail">
                                    <strong>Discovery:</strong> {brand.discovery_context}
                                  </div>
                                )}
                                {brand.psychological_trigger && (
                                  <div className="brand-detail">
                                    <strong>Why it matters:</strong> {brand.psychological_trigger}
                                  </div>
                                )}
                                {brand.current_relationship && (
                                  <div className="brand-detail">
                                    <strong>Current relationship:</strong> {brand.current_relationship}
                                  </div>
                                )}
                                {brand.economic_justification && (
                                  <div className="brand-detail">
                                    <strong>Economic justification:</strong> {brand.economic_justification}
                                  </div>
                                )}
                                {brand.social_performance_tension && (
                                  <div className="brand-tension">
                                    <strong>Tension:</strong> {brand.social_performance_tension}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Consumption Patterns */}
            {profileData.musical_autobiography.consumption_patterns && (
              <div className="consumption-patterns">
                <h3>Consumption Patterns</h3>
                <p>{profileData.musical_autobiography.consumption_patterns}</p>
              </div>
            )}
          </div>
        )}

        {/* Playlist Evolution */}
        {profileData.musical_autobiography?.playlist_evolution && profileData.musical_autobiography.playlist_evolution.length > 0 && (
          <div className="profile-section">
            <h2>Playlist Evolution</h2>
            <div className="playlist-timeline">
              {profileData.musical_autobiography.playlist_evolution.map((playlist, index) => (
                <div key={index} className="playlist-card">
                  <h3>"{playlist.playlist_name}"</h3>
                  <span className="playlist-period">{playlist.period}</span>
                  {playlist.example_tracks && playlist.example_tracks.length > 0 && (
                    <div className="playlist-tracks">
                      <h4>Example Tracks</h4>
                      <ul>
                        {playlist.example_tracks.map((track, trackIndex) => (
                          <li key={trackIndex}>{track}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="playlist-meta">
                    {playlist.artist_criteria && (
                      <p><strong>Artist Criteria:</strong> {playlist.artist_criteria}</p>
                    )}
                    {playlist.psychological_function && (
                      <p><strong>Psychological Function:</strong> {playlist.psychological_function}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Research Obsessions */}
        {profileData.musical_autobiography?.research_obsessions && profileData.musical_autobiography.research_obsessions.length > 0 && (
          <div className="profile-section">
            <h2>Research Obsessions</h2>
            <div className="obsessions-list">
              {profileData.musical_autobiography.research_obsessions.map((obsession, index) => (
                <div key={index} className="obsession-card">
                  <h3>{obsession.focus}</h3>
                  <p className="obsession-manifestation">{obsession.manifestation}</p>
                  <p className="obsession-behaviors"><strong>Behaviors:</strong> {obsession.specific_behaviors}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Streaming Behaviors */}
        {profileData.musical_autobiography?.streaming_behaviors && profileData.musical_autobiography.streaming_behaviors.length > 0 && (
          <div className="profile-section">
            <h2>Streaming Behaviors</h2>
            <div className="streaming-list">
              {profileData.musical_autobiography.streaming_behaviors.map((stream, index) => (
                <div key={index} className="streaming-card">
                  <h3>{stream.pattern}</h3>
                  <div className="streaming-trigger">
                    <span className="trigger-label">Trigger:</span>
                    <span>{stream.trigger}</span>
                  </div>
                  <p className="streaming-behavior">{stream.behavior}</p>
                  {stream.artist_examples && stream.artist_examples.length > 0 && (
                    <div className="streaming-artists">
                      <strong>Artists:</strong> {stream.artist_examples.join(', ')}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
          </div>
        )}

        {/* Brands & Products Tab */}
        {activeTab === 'brands' && (
          <div className="tab-content">

        {/* Brand Consumption Psychology */}
        {profileData.brand_consumption_psychology?.consumption_periods && profileData.brand_consumption_psychology.consumption_periods.length > 0 && (
          <div className="profile-section">
            <h2>Brand Relationships & Consumption</h2>
            <div className="brand-consumption-timeline">
              {profileData.brand_consumption_psychology.consumption_periods.map((period, index) => (
                <div key={index} className="consumption-period">
                  <div className="period-header">
                    <h3>{period.title}</h3>
                    <span className="period-date">{period.period}</span>
                  </div>
                  {period.key_brands && period.key_brands.length > 0 && (
                    <div className="brands-grid">
                      {period.key_brands.map((brand, brandIndex) => (
                        <div key={brandIndex} className="brand-card">
                          <div className="brand-header">
                            <h5>{brand.name}</h5>
                            <span className="brand-category">{brand.category}</span>
                          </div>
                          {brand.current_obsession && (
                            <p className="brand-obsession">{brand.current_obsession}</p>
                          )}
                          {brand.discovery_context && (
                            <div className="brand-detail">
                              <strong>Discovery:</strong> {brand.discovery_context}
                            </div>
                          )}
                          {brand.psychological_trigger && (
                            <div className="brand-detail">
                              <strong>Why it matters:</strong> {brand.psychological_trigger}
                            </div>
                          )}
                          {brand.current_relationship && (
                            <div className="brand-detail">
                              <strong>Current relationship:</strong> {brand.current_relationship}
                            </div>
                          )}
                          {brand.economic_justification && (
                            <div className="brand-detail">
                              <strong>Economic justification:</strong> {brand.economic_justification}
                            </div>
                          )}
                          {brand.social_performance_tension && (
                            <div className="brand-tension">
                              <strong>Tension:</strong> {brand.social_performance_tension}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
          </div>
        )}

        {/* Experiences Tab */}
        {activeTab === 'experiences' && (
          <div className="tab-content">

        {/* Concert Attendance History */}
        {profileData.musical_autobiography?.concert_attendance_history && profileData.musical_autobiography.concert_attendance_history.length > 0 && (
          <div className="profile-section">
            <h2>Concert Attendance History</h2>
            <div className="concert-history">
              {profileData.musical_autobiography.concert_attendance_history.map((concert, index) => (
                <div key={index} className="concert-card">
                  <div className="concert-header">
                    <h3>{concert.artist} @ {concert.venue}</h3>
                    <span className="concert-date">{concert.date}</span>
                  </div>
                  <p className="concert-context"><strong>Context:</strong> {concert.attendance_context}</p>
                  <p className="concert-details"><strong>Details:</strong> {concert.behavioral_details}</p>
                  <div className="concert-significance">
                    <span className="significance-label">Psychological Significance:</span>
                    <p>{concert.psychological_significance}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Venue Ecosystem */}
        {(profileData.enhanced_cultural_context?.venue_ecosystem || profileData.musical_autobiography?.venue_relationship_timeline) && (
          <div className="profile-section">
            <h2>Venue Relationships</h2>
            <div className="venue-list">
              {(profileData.enhanced_cultural_context?.venue_ecosystem || profileData.musical_autobiography?.venue_relationship_timeline)?.map((venue, index) => (
                <div key={index} className="venue-card">
                  <h3>{venue.venue}</h3>
                  {venue.current_situation && (
                    <p className="venue-situation">{venue.current_situation}</p>
                  )}
                  {venue.engagement_patterns && (
                    <p><strong>Engagement:</strong> {venue.engagement_patterns}</p>
                  )}
                  {venue.personal_connection && (
                    <p><strong>Personal Connection:</strong> {venue.personal_connection}</p>
                  )}
                  {venue.discovery_role && (
                    <p><strong>Discovery Role:</strong> {venue.discovery_role}</p>
                  )}
                  {venue.psychological_impact && (
                    <p className="venue-impact"><strong>Psychological Impact:</strong> {venue.psychological_impact}</p>
                  )}
                  {venue.closure_impact && (
                    <p><strong>Significance:</strong> {venue.closure_impact}</p>
                  )}
                  {venue.relationship_period && (
                    <span className="venue-period">{venue.relationship_period}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
          </div>
        )}
      </div>
    </div>
  );
}

export default PersonaProfile;